import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Info, Package, Send, Share2 } from "lucide-react";
import {
  Product,
  productService,
} from "../../../services/product/product.service";
import ProductDetailImageSlider from "../slide/product";

interface CompositionObject {
  values?: string[];
}

interface UtilizationObject {
  values?: string[];
}

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!id) return;
        const { Products } = await productService.getProductsByName(id);
        console.log("data", Products);

        if (Products && Products.length > 0) {
          const productData = await productService.getProductById(
            Products[0].id
          );
          console.log("data", productData);

          setProduct(productData || null);
        } else {
          setError("Product not found");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const renderComposition = () => {
    if (!product) return <li>No product details available.</li>;

    // Check if composition exists and has values
    const compositionValues = product.composition?.values;

    if (!compositionValues || !Array.isArray(compositionValues)) {
      return <li>No composition information available.</li>;
    }

    return compositionValues.map((item: string, index: number) => (
      <li key={index} className="text-gray-600 list-none">
        {item}
      </li>
    ));
  };

  const renderUtilization = () => {
    if (!product) return <li>No product details available.</li>;

    // Check if utilization exists and has values
    const utilizationValues = product.utilization?.values;

    if (!utilizationValues || !Array.isArray(utilizationValues)) {
      return <li>No usage information available.</li>;
    }

    return utilizationValues.map((item: string, index: number) => (
      <li key={index} className="text-gray-600 list-none">
        {item}
      </li>
    ));
  };

  const handleQuantityChange = (value: number) => {
    if (value >= 1) {
      setQuantity(value);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const formatPrice = (price: string | number): string => {
    const numericPrice = typeof price === "string" ? parseFloat(price) : price;
    return !isNaN(numericPrice)
      ? `Rp ${numericPrice.toLocaleString()}`
      : "Price not available";
  };

  const handleSendWhatsApp = () => {
    if (!product) return;

    const totalPrice =
      (typeof product.price === "string"
        ? parseFloat(product.price)
        : product.price) * quantity;

    const message = `Halo, saya tertarik dengan produk:\n\nNama: ${
      product.name
    }\nHarga: ${formatPrice(
      product.price
    )}\nJumlah: ${quantity}\nTotal: ${formatPrice(totalPrice)}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = "+6282130001643";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-  2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error || "Product not found"}
          </h2>
          <button
            onClick={handleGoBack}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Kembali
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-50 bg-white shadow-sm backdrop-blur-sm bg-white/90">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleGoBack}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Kembali
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="md:flex">
          <div className="md:w-1/2">
  {product.images && product.images.length > 0 ? (
    <ProductDetailImageSlider 
      images={product.images as Array<{ id: number; image: string }>} 
      productName={product.name} 
    />
  ) : (
    <div className="flex items-center justify-center h-full bg-gray-100">
      No images available
    </div>
  )}
</div>

            <div className="md:w-1/2 p-8">
              <div className="flex flex-col h-full">
                <div className="flex-grow">
                  {product.category_name && (
                    <div className="text-sm font-bold text-blue-700 uppercase mb-2 bg-blue-100 px-2 py-1 rounded-full inline-block">
                      {product.category_name}
                    </div>
                  )}

                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {product.name}
                  </h1>
                  <p className="text-gray-500 mb-4">{product.latin_name}</p>

                  <div className="text-3xl font-bold text-blue-600 mb-6">
                    {formatPrice(product.price)}
                  </div>

                  <div className="prose prose-sm text-gray-600 mb-8">
                    <p className="leading-relaxed">{product.description}</p>
                  </div>

                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Jumlah
                    </label>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleQuantityChange(quantity - 1)}
                        className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
                        disabled={quantity <= 1}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) =>
                          handleQuantityChange(parseInt(e.target.value) || 1)
                        }
                        className="w-20 text-center border border-gray-300 rounded-lg py-2"
                      />
                      <button
                        onClick={() => handleQuantityChange(quantity + 1)}
                        className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={handleSendWhatsApp}
                    className="w-full bg-green-600 text-white py-4 px-6 rounded-xl hover:bg-green-700 transition-colors duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Pesan via WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-8 space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Deskripsi</h2>
            <div className="prose max-w-none">
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Detail Produk
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Package className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Area</h4>
                    <p className="text-gray-600">{product.area_name}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Familia</h4>
                    <p className="text-gray-600">{product.familia}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Bagian yang Digunakan
                  </h4>
                  <p className="text-gray-600">{product.part_used}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Metode Reproduksi
                  </h4>
                  <p className="text-gray-600">
                    {product.method_of_reproduction}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Umur Panen</h4>
                  <p className="text-gray-600">{product.harvest_age}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Manfaat & Penggunaan
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Khasiat
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {product.efficacy}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Cara Penggunaan
                </h3>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  {renderUtilization()}
                </ul>
              </div>

              {product?.composition && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Komposisi
                  </h3>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    {renderComposition()}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProductDetail;
